import { ChangeDetectionStrategy, Component, OnInit, Signal, computed, signal } from '@angular/core';
import { UserApi } from 'api/user/user-api.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { IFileFormFieldCropOptions } from 'lib/ui/form/fields/file-form-field.component.js';
import { SelectItemField } from 'lib/ui/form/fields/select-item-field.component.js';
import { FormSaveHandler } from 'lib/ui/form/form-save-button.component.js';
import { map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

@Component({
	selector: 'mod-edit-client',
	templateUrl: './edit-client.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class EditClientComponent extends TypeCoreComponent<UserDb.IUserItem> implements OnInit {
	public readonly form = {
		firstName: signal<string | undefined>(undefined),
		lastName: signal<string | undefined>(undefined),
		location: signal<string | undefined>(undefined),
		weightKg: signal<number | undefined>(undefined),
		heightCm: signal<number | undefined>(undefined),
		preferredUnit: signal<UserDb.MeasurementUnitType>('metric'),
		gender: signal<UserDb.UserGender | undefined>(undefined),
		isDisabled: signal<boolean>(false)
	};

	public readonly imageCrop: IFileFormFieldCropOptions = {
		aspectRatio: 'square',
		resizeToWidth: 200
	};

	public readonly isValidForm: Signal<boolean> = computed(() => {
		return true;
	});

	public readonly isEditForm: Signal<boolean> = computed(() => {
		if (this.sharedData()?.data.id) {
			return true;
		}
		return false;
	});

	public readonly saveHandler = computed<FormSaveHandler>(() => {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return undefined;
		}

		const formData: UserApi.IEditUserSettingsRequest = {
			firstName: this.form.firstName(),
			lastName: this.form.lastName(),
			location: this.form.location(),
			weightKg: this.form.weightKg(),
			heightCm: this.form.heightCm(),
			preferredUnit: this.form.preferredUnit(),
			gender: this.form.gender(),
			isDisabled: this.form.isDisabled()
		};

		return this.dependencies.httpService
			.post<UserApi.IUserResponseWithToken, UserApi.IEditUserSettingsRequest>((builder) =>
				builder.withCustomApiRoute('user', 'editSettings').withData(formData).withId(sharedData.data.id).build()
			)
			.pipe(
				map((response) => {
					super.updateSharedData({
						data: response.data.user,
						linkedItems: response.linkedItems
					});
					super.notifySaved('Client');
				})
			);
	});

	public readonly preferredUnitSelectItems: readonly SelectItemField<UserDb.MeasurementUnitType>[] = [
		{
			title: 'Imperial',
			value: 'imperial',
			data: 'imperial'
		},
		{
			title: 'Metric',
			value: 'metric',
			data: 'metric'
		}
	];

	public readonly genderSelectItems: readonly SelectItemField<UserDb.UserGender>[] = [
		{
			title: 'Male',
			value: 'male',
			data: 'male'
		},
		{
			title: 'Female',
			value: 'female',
			data: 'female'
		}
	];

	constructor() {
		super();

		this.sharedDataInit = (sharedData) => {
			this.form.firstName.set(sharedData?.data?.firstName);
			this.form.lastName.set(sharedData?.data?.lastName);
			this.form.location.set(sharedData?.data?.location);
			this.form.weightKg.set(sharedData?.data?.weightKg);
			this.form.heightCm.set(sharedData?.data?.heightCm);
			this.form.preferredUnit.set(sharedData?.data?.preferredUnit ?? 'metric');
			this.form.gender.set(sharedData?.data?.gender);
			this.form.isDisabled.set(sharedData?.data?.isDisabled ?? false);
		};
	}

	handleDeleteClient(): void {
		const client = this.sharedData()?.data;

		if (!client) {
			return;
		}

		super.subscribeToObservable(
			this.dependencies.httpService.deleteMultiple('user', [client.id]).pipe(
				map((response) => {
					// notify delete
					this.notifyDeleted('Client');

					// go to client list
					this.dependencies.navigateService
						.actionRoute((appRoutes) => appRoutes.trainerClientRoutes.clients.children.list)
						.navigate();
				})
			)
		);
	}
}
